<template>
  <div class="position-relative">
    <b-card class="mt-4 shadow">
    
      <!-- Search Student -->
      <div class="mb-4" style="max-width: 450px;">
        <label class="font-weight-bold">Search by Student Email:</label>
        <div class="input-group">
          <input v-model="searchEmail" type="text" class="form-control" placeholder="Enter Student Email"
            @keyup.enter="retrieveStudentLedger" />
          <span class="input-group-text bg-light" style="cursor: pointer;" @click="retrieveStudentLedger">
            <i class="fas fa-search text-primary"></i>
          </span>
        </div>
      </div>
    </b-card>

    <!-- Error message -->
    <div v-if="isTransactionNotFound || (transactionDatas && transactionDatas.length ==0)" class="alert alert-danger mt-3">
      No ledger found for the student with this email.
    </div>

    <!-- Due Amount -->
    <div v-if="transactionDatas && transactionDatas.length > 0">
      <div v-if="calculateDueAmount > 0" class="alert alert-danger font-weight-bold text-center mb-4">
        DUE AMOUNT: ₹{{ calculateDueAmount }}
      </div>
      <div v-else class="alert alert-success font-weight-bold text-center mb-4">
        NO DUES PENDING ✅
      </div>

      <!-- Student Details -->
      <div class="mb-4">
        <h5 class="border-bottom pb-2 mb-3 font-weight-bold">Student Information</h5>
        <div class="row" v-if="transactionDatas && transactionDatas.length > 0">
          <div class="col-md-6 mb-2" v-for="(label, key) in studentFields" :key="key">
            <div>
              <strong style="display: inline-block; margin-right: 4px;">{{ label }}:</strong>
              <span>{{ getStudentFieldValue(key) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Fee Table -->
      <div class="mb-4">
        <h5 class="border-bottom pb-2 font-weight-bold">Fee Details</h5>
        <div class="table-responsive">
          <table class="table table-sm table-bordered">
            <thead class="thead-light">
              <tr>
                <th>Component</th>
                <th class="text-right">Debit (₹)</th>
                <th class="text-right">Credit (₹)</th>
                <th class="text-right">Balance (₹)</th>
                <th class="text-right">Collected Fee (₹)</th>
                <th class="text-right">Remaining Balance (₹)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(group, fee_component_id) in groupedTransactions" :key="fee_component_id">
                <td>{{ group[0].fee_component.name }}</td>
                <td class="text-right">{{ group[0].fee_component.amount }}</td>
                <td class="text-right">{{ formatAmount(getTotal(group, 'credit_amount')) }}</td>
                <td class="text-right">
                  {{ formatAmount(group[0].fee_component.amount - getTotal(group, 'credit_amount')) }}
                </td>
                <td class="text-right">
                  <input
                    v-model.number="collectedFees[fee_component_id]"
                    type="number"
                    class="form-control"
                    :min="0"
                    :max="remainingBalance(fee_component_id)"
                    @input="validateCollectedFee(fee_component_id)"
                    placeholder="Enter Collected Fee"
                  />
                  <small class="text-danger" v-if="collectedFeeErrors[fee_component_id]">
                    {{ collectedFeeErrors[fee_component_id] }}
                  </small>
                </td>
                <td class="text-right">
                  {{ formatAmount(remainingBalance(fee_component_id)) }}
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td class="font-weight-bold">Total</td>
                <td class="text-right font-weight-bold">{{ formatAmount(totalDebit) }}</td>
                <td class="text-right font-weight-bold">{{ formatAmount(totalCredit) }}</td>
                <td class="text-right font-weight-bold">{{ formatAmount(totalBalance) }}</td>
                <td class="text-right font-weight-bold">{{ formatAmount(totalCollected) }}</td>
                <td class="text-right font-weight-bold">{{ formatAmount(totalRemaining) }}</td>
              </tr>
            </tfoot>
          </table>

          <span v-if="totalCollected > totalBalance" class="text-danger font-weight-bold d-block text-center mt-3">
            Warning: Collected amount exceeds the total balance.
          </span>

          <div class="text-center mt-4" v-if="totalCollected > 0 && totalCollected <= totalBalance">
            <b-button @click="openCollectFeeModal" variant="success">Collect Fee</b-button>
          </div>
        </div>
      </div>

      <!-- Collect Fee Modal -->
      <b-modal v-model="showCollectFeeModal" title="Collect Fee" no-close-on-backdrop no-close-on-esc hide-footer
        centered size="md">
        <div class="mb-3">
          <label><strong>Collected Amount:</strong></label>
          <p class="text-success font-weight-bold bg-light p-2">₹{{ totalCollected }}</p>
        </div>
        <div class="mb-3">
          <label><strong>Remaining Amount:</strong></label>
          <p>₹{{ totalRemaining }}</p>
        </div>
        <div class="mb-3">
          <label><strong>Payment Mode:</strong></label>
          <b-form-radio-group v-model="paymentMode" :options="paymentModes" :state="!errors.paymentMode" />
          <small class="text-danger" v-if="errors.paymentMode">Please select a payment mode.</small>
        </div>
        <div class="mb-3">
          <label><strong>Collected Date:</strong></label>
          <p>{{ formattedDate }}</p>
        </div>
        <div class="mb-3">
          <label><strong>Collected By:</strong></label>
          <p>{{ profile.first_name }} {{ profile.last_name }}</p>
        </div>
        <div class="text-center">
          <b-button @click="submitPayment" variant="primary">Submit</b-button>
        </div>
      </b-modal>
    </div>
    <span v-else-if="isTransactionNotFound">
      No transactions found.
    </span>
  </div>
</template>

<script>
export default {
  data() {
    return {
      searchEmail: '',
      transactionDatas: null,
      isTransactionNotFound: false,
      collectedFees: {},
      collectedFeeErrors: {},
      showCollectFeeModal: false,
      paymentMode: '1',
      collectedDate: new Date(),
      errors: {},
      paymentModes: [
        { value: '1', text: 'Cash' },
        { value: '2', text: 'Bank Transfer' },
        { value: '3', text: 'Cheque' },
      ],
      studentFields: {
        full_name: 'Full Name',
        admission_no: 'Admission Number',
        fee_category: 'Fee Category',
        academic_info: 'Academic Year',
        email: 'Email',
        mobile: 'Mobile Number',
      },
    };
  },

  computed: {
    profile() {
      return this.$store.getters.getMyProfile;
    },
    formattedDate() {
      const d = new Date(this.collectedDate);
      return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
    },
    groupedTransactions() {
      const grouped = {};
      this.transactionDatas?.forEach((tx) => {
        const id = tx.fee_component.id;
        this.$set(this.collectedFees, id, 0);
        if (!grouped[id]) grouped[id] = [];
        grouped[id].push(tx);
      });

      return grouped;
    },
    calculateDueAmount() {
      const tx = this.transactionDatas || [];
      const components = tx[0]?.fee_student_category_mapping?.feecategory?.components || [];
      const debit = components.reduce((sum, comp) => sum + parseFloat(comp.amount || 0), 0);
      const credit = tx.reduce((s, t) => s + parseFloat(t.credit_amount || 0), 0);
      return (debit - credit).toFixed(2);
    },
    totalDebit() {
      return Object.values(this.groupedTransactions).reduce((sum, group) => {
        const amount = group[0].fee_component.amount || 0;
        return Number(sum) + Number(amount);
      }, 0);
    },
    totalCredit() {
      return Object.values(this.groupedTransactions).reduce((s, g) => s + this.getTotal(g, 'credit_amount'), 0);
    },
    totalBalance() {
      return this.totalDebit - this.totalCredit;
    },
    totalCollected() {
      return Object.values(this.collectedFees).reduce((sum, v) => sum + (v || 0), 0);
    },
    totalRemaining() {
      return this.totalBalance - this.totalCollected;
    },
  },
  methods: {
    validateCollectedFee(fee_component_id) {
      const balance = this.remainingBalance(fee_component_id);
      if (balance < 0) {
        this.$set(this.collectedFeeErrors, fee_component_id, 'Collected fee cannot exceed remaining balance.');
      } else {
        this.$delete(this.collectedFeeErrors, fee_component_id);
      }
    },
    getStudentFieldValue(field) {
      if (!this.transactionDatas || !this.transactionDatas[0]) return '';

      const user = this.transactionDatas[0].fee_student_category_mapping.institution_user;
      const cat = this.transactionDatas[0].fee_student_category_mapping.feecategory;

      switch (field) {
        case 'full_name': return `${user.first_name} ${user.last_name}`;
        case 'admission_no': return user.admission_no;
        case 'fee_category': return cat.name;
        case 'academic_info': return `${cat.academic_year} - ${cat.academic_semester}`;
        case 'email': return user.email;
        case 'mobile': return user.mobile;
        default: return '';
      }
    },
    formatAmount(amount) {
      return Number(amount || 0).toLocaleString('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 2,
      });
    },
    getTotal(arr, key) {
      return arr.reduce((sum, t) => sum + Number(t[key] || 0), 0);
    },
    remainingBalance(id) {
      const g = this.groupedTransactions[id] || [];
      const debit = g[0].fee_component.amount;
      const credit = this.getTotal(g, 'credit_amount');
      const collected = this.collectedFees[id] || 0;
      return debit - credit - collected;
    },
    getComponentRemainingMap() {
      const map = {};
      Object.keys(this.groupedTransactions).forEach(fee_component_id => {
        map[fee_component_id] = this.formatAmount(
          this.remainingBalance(fee_component_id)
        );
      });
      return map;
    },
    openCollectFeeModal() {
      this.errors = {};
      this.showCollectFeeModal = true;
    },
    async submitPayment() {
      this.errors = {};
      if (!this.paymentMode) {
        this.errors.paymentMode = true;
        return;
      }
      const componentRemaining = this.getComponentRemainingMap();
      console.log("componentRemaining", componentRemaining);
      // Iterate over each key-value pair in the componentRemaining object
      for (const key in componentRemaining) {
        // Use Object.hasOwn to check if the key belongs directly to the object
        if (Object.hasOwn(componentRemaining, key)) {
          // Remove the currency symbol "₹" and keep the rest of the value
          componentRemaining[key] = componentRemaining[key].replace('₹', '').replace(',', '').trim();
        }
      }
      console.log("componentRemaining2", componentRemaining);

      console.log("this.collectedFees", this.collectedFees);

      try {
        const url = `${this.$store.getters.getAPIKey.mainAPI}/studentcategorymappings/collect-fee`;
        await this.$axios.post(url, {
          fee_ledger_id: this.transactionDatas[0].fee_ledger_id,
          studentCategoryMapId: this.transactionDatas[0].fee_student_category_mapping.id,
          componentFeeMappings: this.collectedFees,
          componentRemaining: componentRemaining,
          payment_mode: this.paymentMode,
          fee_collected: this.totalCollected,
          balance_amount: this.totalRemaining,
        });
        this.$toast.success('Fee Collected', { position: 'top', duration: 3000 });
        this.retrieveStudentLedger();
        this.transactionDatas = null;
        this.collectedFees = {};
        this.showCollectFeeModal = false;
        this.paymentMode = '1';
        this.collectedDate = new Date();
        this.errors = {};
        this.isTransactionNotFound = false;
      } catch (err) {
        console.error('Payment submission failed:', err);
      }
    },
    async retrieveStudentLedger() {
      this.isTransactionNotFound = false;
      this.transactionDatas = null;
      try {
        const url = `${this.$store.getters.getAPIKey.mainAPI}/studentcategorymappings/view-transaction`;
        const response = await this.$axios.get(url, {
          params: { email: this.searchEmail.trim() },
        });
        if (response.data) {
          this.transactionDatas = response.data;
          this.collectedFees = {};
        } else {
          this.isTransactionNotFound = true;
        }
      } catch (err) {
        console.error('Error fetching student ledger:', err);
        this.isTransactionNotFound = true;
      }
    },
  },
};
</script>